import { GridFooterContainer, GridPagination } from "@mui/x-data-grid";
import { Box, TextField, Tooltip, IconButton, Chip, Typography } from "@mui/material";
import { useState, useMemo, useCallback } from "react";
import { AiOutlineEdit, AiOutlineCheck, AiOutlineClose } from "react-icons/ai";

export default function JumpByPageFooter({
  total,
  page,
  pageSize,
  onJumpPage,
  noun = "registro",
  nounPlural,
  formatPageLabel,
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");

  const defaultPlural = (s) => {
    if (!s) return "registros";
    if (s.endsWith("z")) return s.slice(0, -1) + "ces"; // p.ej. "vez" -> "veces"
    if (s.endsWith("s")) return s;                      // ya es plural o invariable
    return s + "s";
  };

  const pluralNoun = nounPlural || defaultPlural(noun);

  const pageCount = useMemo(() => {
    const t = Math.max(0, total || 0);
    const size = Math.max(1, pageSize || 1);
    return t === 0 ? 1 : Math.ceil(t / size);
  }, [total, pageSize]);

  const pageLabel = formatPageLabel
    ? formatPageLabel(Math.min(page + 1, pageCount), pageCount)
    : `Página ${Math.min(page + 1, pageCount)} de ${pageCount}`;

  const invalid =
    editing &&
    value !== "" &&
    (Number.isNaN(Number(value)) || Number(value) < 1 || Number(value) > pageCount);

  const applyJump = useCallback(() => {
    const raw = parseInt(value, 10);
    if (Number.isNaN(raw)) {
      setEditing(false);
      return;
    }
    const clamped = Math.max(1, Math.min(raw, pageCount));
    onJumpPage?.(clamped - 1); // 0-based
    setEditing(false);
  }, [value, pageCount, onJumpPage]);

  return (
    <GridFooterContainer sx={{ px: 1 }}>
      <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
        {/* Total (solo informativo) */}
        <Typography
          variant="body2"
          sx={{ color: "text.primary", whiteSpace: "nowrap", mr: 1 }}
          aria-label={`total de ${pluralNoun}`}
        >
          {total ?? 0} {total === 1 ? noun : pluralNoun}
        </Typography>

        {/* Editor de página */}
        {!editing ? (
          <Tooltip title="Click para escribir un número de página">
            <Box
              onClick={() => {
                setValue(String(Math.min(page + 1, pageCount)));
                setEditing(true);
              }}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
              aria-label="editar número de página"
            >
              <Chip
                variant="outlined"
                label={pageLabel}
                sx={{
                  borderRadius: "999px",
                  "& .MuiChip-label": { px: 1.25, py: 0.25, fontSize: 13 },
                }}
              />
            </Box>
          </Tooltip>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <TextField
              autoFocus
              size="small"
              type="number"
              inputProps={{ min: 1, max: pageCount }}
              placeholder="Ir a pág. #"
              value={value}
              error={invalid}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !invalid) applyJump();
                if (e.key === "Escape") setEditing(false);
              }}
              sx={{ width: 140 }}
            />
            <IconButton size="small" color="success" onClick={applyJump} disabled={invalid}>
              <AiOutlineCheck size={18} />
            </IconButton>
            <IconButton size="small" color="default" onClick={() => setEditing(false)}>
              <AiOutlineClose size={18} />
            </IconButton>
          </Box>
        )}

        {/* Flechas del paginador; sin textos extra */}
        <Box
          sx={{
            ml: 0,
            "& .MuiTablePagination-displayedRows": { display: "none" },
            "& .MuiTablePagination-root": { p: 0, m: 0 },
            "& .MuiTablePagination-toolbar": { p: 0, minHeight: 36 },
            "& .MuiTablePagination-spacer": { display: "none" },
            "& .MuiTablePagination-selectLabel": { display: "none" },
            "& .MuiTablePagination-input": { display: "none" },
            "& .MuiTablePagination-actions": { ml: 0 },
          }}
        >
          <GridPagination labelDisplayedRows={() => ""} />
        </Box>
      </Box>
    </GridFooterContainer>
  );
}
